import { SignedIn, SignedOut, SignInButton, UserButton } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

/**
 * AuthButton - Header authentication component
 *
 * Shows "Sign In" button for anonymous users, or Dashboard link + UserButton for authenticated users.
 * Uses Clerk's prebuilt components with modal-based sign-in flow.
 *
 * @param {Object} props
 * @param {string} props.variant - 'default' (inline text) or 'mobile' (full-width button)
 */
export default function AuthButton({ variant = 'default' }) {
  const navigate = useNavigate();
  const isMobile = variant === 'mobile';

  // Inline text link style (desktop header)
  const inlineButtonClass = "text-sm font-medium text-navy-700 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors";

  // Full-width button style (mobile menu)
  const mobileButtonClass = "w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold bg-navy-600 hover:bg-navy-700 active:bg-navy-800 text-white transition-colors";
  const mobileDashboardClass = "w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors";

  return (
    <>
      <SignedOut>
        <SignInButton mode="modal">
          <button
            className={isMobile ? mobileButtonClass : inlineButtonClass}
            style={isMobile ? { WebkitTapHighlightColor: 'transparent' } : undefined}
          >
            {isMobile && (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            )}
            Sign In
          </button>
        </SignInButton>
      </SignedOut>
      <SignedIn>
        {isMobile ? (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className={mobileDashboardClass}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Dashboard
            </button>
            <div className="flex items-center justify-center py-2">
              <UserButton
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: "w-10 h-10"
                  }
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className={inlineButtonClass}
            >
              Dashboard
            </button>
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: "w-8 h-8"
                }
              }}
            />
          </div>
        )}
      </SignedIn>
    </>
  );
}
